/**
 * Modal shown when a recipe import fails because the recipe site requires login.
 */
import { useState, useCallback } from 'react'
import { authenticatedFetch } from '../utils/auth.js'
import { getCookieCopyScript } from '../utils/recipeAuthScript.js'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '../design-system/primitives/index.js'
import './RecipeAuthModal.css'

function parsePastedCookies(pasted, domainFallback) {
  const trimmed = (pasted || '').trim()
  if (!trimmed) return null

  try {
    const parsed = JSON.parse(trimmed)
    if (parsed && typeof parsed.cookies === 'object' && Object.keys(parsed.cookies).length > 0) {
      return {
        domain: (parsed.domain && String(parsed.domain).trim()) || domainFallback,
        cookies: parsed.cookies,
      }
    }
    if (parsed && typeof parsed === 'object' && !parsed.cookies && !Array.isArray(parsed)) {
      const keys = Object.keys(parsed)
      if (keys.length > 0) {
        return { domain: domainFallback, cookies: parsed }
      }
    }
  } catch {
    // Not JSON
  }

  const cookies = {}
  const parts = trimmed.split(/[;\n]/).map((p) => p.trim()).filter(Boolean)
  for (const part of parts) {
    const eq = part.indexOf('=')
    if (eq > 0) {
      const name = part.slice(0, eq).trim()
      const value = part.slice(eq + 1).trim().replace(/^["']|["']$/g, '')
      if (name) cookies[name] = value
    }
  }
  if (Object.keys(cookies).length > 0) {
    return { domain: domainFallback, cookies }
  }
  return null
}

export default function RecipeAuthModal({ domain, onClose, onImported }) {
  const [step, setStep] = useState('prompt')
  const [pasted, setPasted] = useState('')
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState('')
  const [copyScriptDone, setCopyScriptDone] = useState(false)

  const siteUrl = domain ? `https://${domain.startsWith('www.') ? domain : `www.${domain}`}` : null
  const siteUrlNoWww = domain ? `https://${domain.replace(/^www\./, '')}` : null

  const handleOpenSite = useCallback(() => {
    const url = siteUrlNoWww || siteUrl || `https://${domain}`
    window.open(url, '_blank', 'noopener,noreferrer')
    setStep('capture')
  }, [domain, siteUrl, siteUrlNoWww])

  const handleCopyScript = useCallback(() => {
    const script = getCookieCopyScript()
    navigator.clipboard.writeText(script).then(() => setCopyScriptDone(true))
  }, [])

  const handleImportCookies = useCallback(async () => {
    const parsed = parsePastedCookies(pasted, domain)
    if (!parsed) {
      setImportError('Paste the cookie output from the script (JSON or name=value lines), then try again.')
      return
    }
    setImportError('')
    setImporting(true)
    try {
      const res = await authenticatedFetch('/recipes/auth/import-cookies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain: parsed.domain,
          cookies: parsed.cookies,
          expires_in_days: 30,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error(data.detail || data.message || `Import failed (${res.status})`)
      }
      onImported?.()
      onClose?.()
    } catch (e) {
      setImportError(e.message || 'Failed to import cookies.')
    } finally {
      setImporting(false)
    }
  }, [pasted, domain, onImported, onClose])

  const scriptSnippet = getCookieCopyScript()
  const canImport = pasted.trim().length > 0

  return (
    <Modal
      open
      onClose={onClose}
      titleId="recipe-auth-modal-title"
      overlayClassName="recipe-auth-modal-overlay"
      contentClassName="recipe-auth-modal-content"
    >
      <ModalHeader>
        <h2 id="recipe-auth-modal-title">
          Authenticate for {domain || 'this recipe site'}
        </h2>
      </ModalHeader>

      <ModalBody>
        {step === 'prompt' && (
          <>
            <p className="recipe-auth-intro">
              This recipe site requires you to be logged in. Open the site, log in if needed, then capture your cookies and paste them here.
            </p>
            <div className="recipe-auth-stack">
              <Button type="button" variant="primary" onClick={handleOpenSite}>
                Open site & log in
              </Button>
              <Button type="button" variant="secondary" onClick={() => setStep('capture')}>
                I&apos;m already logged in
              </Button>
            </div>
          </>
        )}

        {step === 'capture' && (
          <>
            <p className="recipe-auth-step-copy">
              Step 1: Open <strong>{domain}</strong> in a tab and make sure you&apos;re logged in.
            </p>
            <p className="recipe-auth-step-copy recipe-auth-step-copy--tight">
              Step 2: Press <kbd className="recipe-auth-kbd">F12</kbd> to open Developer Tools → <strong>Console</strong>. Paste the script below and press Enter. It will copy your cookies to the clipboard.
            </p>
            <div className="recipe-auth-script-wrap">
              <pre className="recipe-auth-script-pre">{scriptSnippet}</pre>
              <Button
                type="button"
                variant="secondary"
                className="recipe-auth-copy-btn"
                onClick={handleCopyScript}
              >
                {copyScriptDone ? '✓ Copied' : 'Copy script'}
              </Button>
            </div>
            <p className="recipe-auth-step-copy recipe-auth-step-copy--tight">
              Step 3: Paste below (from clipboard) and click Import.
            </p>
            <textarea
              className="recipe-auth-textarea"
              value={pasted}
              onChange={(e) => { setPasted(e.target.value); setImportError('') }}
              placeholder="Paste the copied JSON or cookie lines here (e.g. name=value; ...)"
              rows={4}
            />
            {importError && (
              <p className="recipe-auth-error" role="alert">
                {importError}
              </p>
            )}
            <p className="recipe-auth-footnote">
              If import still fails, the site may use protected cookies. Use DevTools → Application → Cookies to copy name/value pairs and paste here.
            </p>
            <div className="recipe-auth-actions">
              <Button
                type="button"
                variant="primary"
                onClick={handleImportCookies}
                disabled={importing || !canImport}
              >
                {importing ? 'Importing…' : 'Import cookies'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setStep('prompt')}>
                Back
              </Button>
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}
